/**
 * 承認履歴タイムラインコンポーネント
 * ビルドの承認/却下履歴を時系列で表示
 */

import React from 'react';
import { Icon } from './icons';

// アクションタイプの定義
const ACTION_TYPES = {
  BUILD_STARTED: {
    icon: 'play',
    color: 'bg-blue-500',
    label: 'ビルド開始',
  },
  BUILD_SUCCEEDED: {
    icon: 'check',
    color: 'bg-green-500',
    label: 'ビルド成功',
  },
  BUILD_FAILED: {
    icon: 'x',
    color: 'bg-red-500',
    label: 'ビルド失敗',
  },
  TEST_STARTED: {
    icon: 'activity',
    color: 'bg-blue-500',
    label: 'テスト開始',
  },
  TEST_PASSED: {
    icon: 'check-circle',
    color: 'bg-green-500',
    label: 'テスト合格',
  },
  TEST_FAILED: {
    icon: 'x-circle',
    color: 'bg-red-500',
    label: 'テスト失敗',
  },
  APPROVAL_REQUESTED: {
    icon: 'clock',
    color: 'bg-yellow-500',
    label: '承認待ち',
  },
  APPROVED: {
    icon: 'check',
    color: 'bg-green-500',
    label: '承認',
  },
  REJECTED: {
    icon: 'x',
    color: 'bg-red-500',
    label: '却下',
  },
  ARTIFACT_TRANSFERRED: {
    icon: 'upload',
    color: 'bg-purple-500',
    label: '成果物転送',
  },
};

function formatDateTime(isoStr) {
  if (!isoStr) return '-';
  const date = new Date(isoStr);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(isoStr) {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  const now = new Date();
  const diff = now - date;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  if (hours < 24) return `${hours}時間前`;
  if (days < 7) return `${days}日前`;
  return formatDateTime(isoStr);
}

function TimelineItem({ event, isLast }) {
  const actionType = ACTION_TYPES[event.action] || {
    icon: 'circle',
    color: 'bg-slate-400',
    label: event.action,
  };

  return (
    <div className="relative flex gap-4">
      {/* タイムラインの線 */}
      {!isLast && (
        <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-slate-200" />
      )}

      {/* アイコン */}
      <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full ${actionType.color} flex items-center justify-center`}>
        <Icon name={actionType.icon} className="w-4 h-4 text-white" />
      </div>

      {/* コンテンツ */}
      <div className="flex-1 pb-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-900">
            {actionType.label}
          </p>
          <time className="text-xs text-slate-400" title={formatDateTime(event.timestamp)}>
            {formatRelativeTime(event.timestamp)}
          </time>
        </div>

        {/* 実行者 */}
        {event.user && (
          <p className="text-sm text-slate-600 mt-1">
            <span className="text-slate-400">実行者:</span> {event.user}
          </p>
        )}

        {/* コメント */}
        {event.comment && (
          <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">{event.comment}</p>
          </div>
        )}

        {/* 詳細情報 */}
        {event.details && (
          <div className="mt-2 text-xs text-slate-500">
            {Object.entries(event.details).map(([key, value]) => (
              <span key={key} className="mr-4">
                <span className="text-slate-400">{key}:</span> {value}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ApprovalTimeline({ events = [], loading = false }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="loader" className="w-6 h-6 text-slate-400 animate-spin" />
        <span className="ml-2 text-sm text-slate-500">履歴を読み込み中...</span>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Icon name="clock" className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">履歴がありません</p>
      </div>
    );
  }

  // タイムスタンプで降順ソート
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <div className="flow-root">
      <div className="-mb-6">
        {sortedEvents.map((event, index) => (
          <TimelineItem
            key={event.id || index}
            event={event}
            isLast={index === sortedEvents.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

// ビルドデータからタイムラインイベントを生成するヘルパー
export function buildToTimelineEvents(build) {
  const events = [];
  const buildId = build.buildId || build.id;

  // ビルド開始
  if (build.startedAt) {
    events.push({
      id: `${buildId}-build-start`,
      action: 'BUILD_STARTED',
      timestamp: build.startedAt,
      user: build.triggeredByUser || build.triggeredBy,
      details: {
        repository: build.repository,
        branch: build.branch,
      },
    });
  }

  // ビルド完了
  if (build.completedAt && build.status !== 'IN_PROGRESS') {
    events.push({
      id: `${buildId}-build-complete`,
      action: build.status === 'SUCCEEDED' ? 'BUILD_SUCCEEDED' : 'BUILD_FAILED',
      timestamp: build.completedAt,
    });
  }

  // SiTLテスト
  if (build.testResults?.sitl) {
    const sitl = build.testResults.sitl;
    if (sitl.startedAt) {
      events.push({
        id: `${buildId}-sitl-start`,
        action: 'TEST_STARTED',
        timestamp: sitl.startedAt,
        details: { type: 'SiTL' },
      });
    }
    if (sitl.completedAt && sitl.status !== 'RUNNING') {
      events.push({
        id: `${buildId}-sitl-complete`,
        action: sitl.status === 'PASSED' ? 'TEST_PASSED' : 'TEST_FAILED',
        timestamp: sitl.completedAt,
        details: {
          type: 'SiTL',
          passed: `${sitl.passed}/${sitl.totalTests}`,
        },
      });
    }
  }

  // 承認
  if (build.approval) {
    if (build.approval.status === 'APPROVED') {
      events.push({
        id: `${buildId}-approved`,
        action: 'APPROVED',
        timestamp: build.approval.approvedAt,
        user: build.approval.approvedBy,
        comment: build.approval.comment,
      });
    } else if (build.approval.status === 'REJECTED') {
      events.push({
        id: `${buildId}-rejected`,
        action: 'REJECTED',
        timestamp: build.approval.rejectedAt,
        user: build.approval.rejectedBy,
        comment: build.approval.comment,
      });
    } else if (build.pipelineStage === 'approval') {
      // 承認待ち状態
      const approvalReadyTime = build.testResults?.sitl?.completedAt || build.completedAt;
      if (approvalReadyTime) {
        events.push({
          id: `${buildId}-approval-requested`,
          action: 'APPROVAL_REQUESTED',
          timestamp: approvalReadyTime,
        });
      }
    }
  }

  return events;
}
